import { zimbaApi, contentApi } from '@/infrastructure/Configuration'
import { getQueryParams, normalizeFilters } from '@/infrastructure/Utils'
import OffersFilter from './OffersFilter'
import OffersFacets from './OffersFacets'
import OffersResponse from './OffersReponse'
import OffersResponseRevamp from './OffersResponseRevamp'
import OffersRegions from '@/services/offers/OffersRegions'
import CountriesAndCities from '@/services/offers/CountriesAndCities'
import Offer from '@/domain/Offer'
import { dnaService } from '@/infrastructure/Factory'

export default class OffersService {
  constructor(httpClient, destinationService, hotelService, loyaltyService) {
    this.httpClient = httpClient
    this.destinationService = destinationService
    this.hotelService = hotelService
    this.loyaltyService = loyaltyService
  }

  async getInitialOffers() {
    const storedFilter = JSON.parse(sessionStorage.getItem('filter'))
    const filter = new OffersFilter(storedFilter || {}).setFirstPage()

    const all = await Promise.all([
      this.destinationService.getOfferHotelCategories(),
      getCountriesAndCitiesFromZimba(this.httpClient),
      getBrandsFromZimba(this.httpClient, this.hotelService),
      getCategoriesFromZimba(this.httpClient),
      getRegionsFromZimba(this.httpClient)
    ]).then(data => ({
      categories: [...data[0]].sort((a, b) => a.name.localeCompare(b.name)),
      countries: data[1],
      brands: data[2],
      offersRevampCategories: data[3],
      regions: data[4]
    }))

    const {
      category,
      country,
      city,
      startDate,
      endDate,
      brand,
      isRewardsOnly,
      region,
      latitude,
      longitude,
      distance
    } = getQueryParams(window.location.search)
    const queryParams = {
      category,
      country,
      city,
      startDate,
      endDate,
      brand,
      isRewardsOnly,
      region,
      latitude,
      longitude,
      distance
    }
    let filterUpdated = filter
    if (queryParams && Object.values(queryParams).some(param => typeof param !== 'undefined')) {
      filterUpdated = OffersFilter.initFromUrl(all, filter, queryParams)
    }
    let categories = all.categories
    categories = all.categories.filter(category =>
      all.offersRevampCategories.includes(category.code)
    )

    let facets = new OffersFacets({
      categories,
      countries: all.countries,
      brands: all.brands
    })
    facets = facets.markSelected(filterUpdated)

    const offersData = await Promise.all([getOffersFromZimba(this.httpClient, filterUpdated)]).then(
      data => ({
        offers: data[0]
      })
    )

    return new OffersResponse({
      total: offersData.offers.total,
      results: offersData.offers.offers,
      facets,
      filter: filterUpdated
    })
  }

  async getInitialOffersRevamp() {
    const countryCode = getCountryCodeFromCookies()
    const featuredAndSliders = await getCategories(this.httpClient, countryCode)
    const allDnaSortEnableFromSliders = [featuredAndSliders?.featuredOffer?.filters?.dnaSortEnable,
      ...featuredAndSliders.offers.map(slider => slider?.filters?.dnaSortEnable)]

    const sliderWithDna = allDnaSortEnableFromSliders.some(dnaSortEnabled => !!dnaSortEnabled)
    const dnaString = await dnaService.getDnaString(sliderWithDna)

    await this.handleGetOffersWithOrWithoutDna(featuredAndSliders, dnaString)
    return new OffersResponseRevamp({
      slides: {
        featuredOffers: { ...featuredAndSliders.featuredOffer },
        offers: featuredAndSliders.offers
      }
    })
  }

  async handleGetOffersWithOrWithoutDna(featuredAndSliders, dna) {
    if (dna && featuredAndSliders?.featuredOffer?.filters?.dnaSortEnable) {
      featuredAndSliders.featuredOffer.offers = await this.getCategoriesOffersWithDna(
        normalizeFilters({ ...featuredAndSliders.featuredOffer.filters, dna })
      )
    } else {
      featuredAndSliders.featuredOffer.offers = await this.getCategoriesOffers(
        normalizeFilters({ ...featuredAndSliders.featuredOffer.filters })
      )
    }
    await Promise.all(
      featuredAndSliders.offers.map(async category => {
        let offersData = {}
        if (dna && category?.filters?.dnaSortEnable) {
          offersData = await this.getCategoriesOffersWithDna(
            normalizeFilters({ ...category.filters, dna })
          )
        } else {
          offersData = await this.getCategoriesOffers(
            normalizeFilters({ ...category.filters })
          )
        }
        category.offers = { ...offersData }
      })
    )
  }

  async getOffers(facets, filter = OffersFilter.empty()) {
    const offers = await getOffersFromZimba(this.httpClient, filter)
    facets = facets.markSelected(filter)

    return new OffersResponse({
      total: offers.total,
      results: offers.offers,
      facets,
      filter
    })
  }

  async getRegions() {
    const regions = await getRegionsFromZimba(this.httpClient)
    return new OffersRegions(regions)
  }

  async getCountriesAndCities() {
    const res = await getCountriesAndCitiesFromZimba(this.httpClient)
    return new CountriesAndCities(res)
  }

  async getCategoriesOffers(filter = new OffersFilter({})) {
    const url = `${zimbaApi}/query/offers?${filter.asQueryParams()}`
    const { data } = await this.httpClient.get(url)
    return data
  }

  async getCategoriesOffersWithDna(filter = new OffersFilter({})) {
    const url = `${zimbaApi}/query/offers-dna?${filter.asQueryParams()}`
    const { data } = await this.httpClient.get(url)
    return data
  }

  async getUserOffers(limit = 5, offset = 0, offerCodes = []) {
    const emptyResponse = {
      limit,
      offset,
      total: 0,
      offers: [],
      offerCodes
    }

    try {
      let codesRegisteredOffers = offerCodes

      if (!codesRegisteredOffers.length) {
        const { registeredOffers } = await this.loyaltyService.getRegisteredOffers()
        codesRegisteredOffers = registeredOffers.map(o => o.backCode)

        if (!codesRegisteredOffers.length) {
          return emptyResponse
        }
      }

      const resOffers = await this.getOffersByCodes(codesRegisteredOffers, limit, offset, {
        sort: 'bookingEndDate'
      })

      return {
        limit,
        offset,
        total: resOffers.total,
        offers: resOffers.offers.map(o => Offer.parse(o)),
        offerCodes: codesRegisteredOffers
      }
    } catch (e) {
      console.error(e)
      return emptyResponse
    }
  }

  async getOffersByCodes(offerCodes = [], limit = 5, offset = 0, other = {}) {
    const params = {
      limit,
      offset,
      ...other
    }
    const strConfig = global.Utils.stringifyQueryParams(params)
    const strCodes = offerCodes.map(code => `backCode=${code}`).join('&')
    const strFields = ['offers', 'total'].map(code => `fields=${code}`).join('&')
    const strParams = [strConfig, strCodes, strFields].join('&')
    const url = `${zimbaApi}/query/offers?${strParams}`
    const { data } = await this.httpClient.get(url)
    return data
  }
}

const getCategories = async (httpClient, countryCode = '') => {
  const countryCodeUrl = countryCode ? `?countryCode=${countryCode}` : ''
  const url = `${contentApi}/offers/home${countryCodeUrl}`
  const { data } = await httpClient.get(url)
  return data
}

const getOffersFromZimba = async (httpClient, filter = new OffersFilter({})) => {
  const url = `${zimbaApi}/query/offers?` + filter.asQueryParams()
  const { data } = await httpClient.get(url)

  return data
}

const getCountriesAndCitiesFromZimba = async httpClient => {
  const url = `${zimbaApi}/query/offers?fields=countries&fields=cities&all=true`
  const { data } = await httpClient.get(url)

  const filterCitiesbyCountry = (cities, country) =>
    Object.keys(cities)
      .filter(city => cities[city].countryOrStateCode === country)
      .map(c => ({ code: c, name: cities[c].name }))

  return Object.keys(data.countries).map(country => ({
    code: country,
    name: data.countries[country].name,
    cities: filterCitiesbyCountry(data.cities, country)
  }))
}

const getCategoriesFromZimba = async httpClient => {
  const url = `${zimbaApi}/query/offers?fields=categories`
  const { data } = await httpClient.get(url)

  return data.categories
}

const getBrandsFromZimba = async (httpClient, hotelService) => {
  const url = `${zimbaApi}/query/offers?fields=brands&all=true`
  const { data } = await httpClient.get(url)
  const brandFromOffers = data.brands

  const brandsFromHotel = await hotelService.getBrands()

  return brandFromOffers.map(code => ({
    code: code,
    name: brandsFromHotel[code] ? brandsFromHotel[code].name : ''
  }))
}

const getRegionsFromZimba = async httpClient => {
  const url = `${zimbaApi}/query/offers?fields=regions&all=true`
  const { data } = await httpClient.get(url)

  return Object.keys(data.regions).map(region => ({
    code: data.regions[region].code,
    name: data.regions[region].name
  }))
}

const getCountryCodeFromCookies = () => {
  return `; ${document.cookie}`
    .split('; rhg-device-info=')
    .pop()
    .split(';')
    .shift()
    .split('&country=')
    .pop()
    .split('&')
    .shift()
}
